import { isPastExpirationTime } from '../../utilities/dateUtils';
import { Quote } from './quoteTypes';

export const isQuoteExpired = (
  expirationTime: number,
  status?: string,
  isInternalUser?: boolean,
  createdAt?: string
) => {
  if (
    status === undefined ||
    isInternalUser === undefined ||
    createdAt === undefined
  ) {
    return false;
  }
  if (isInternalUser && status === 'approved') {
    return false;
  }
  return (
    (status === 'approved' || status === 'countered') &&
    isInternalUser === true &&
    isPastExpirationTime(expirationTime, createdAt)
  );
};

export const isVerifying = (status?: string, isInternalUser?: boolean) => {
  return status === 'approved' && isInternalUser === false;
};

export const isBooked = (status?: string) => {
  return status === 'completed';
};

export const isQuoteAdminAccepted = (expirationTimer: number, quote: Quote) => {
  return (
    quote.status === 'approved' &&
    quote.isInternalUser === true &&
    !isPastExpirationTime(expirationTimer, quote.createdAt)
  );
};

export const isQuotePending = (expirationTimer: number, quote: Quote) => {
  return (
    (quote.status !== 'denied' &&
      quote.status !== 'completed' &&
      quote.status !== 'approved') ||
    isQuoteCountered(expirationTimer, quote)
  );
};

export const isQuoteAdminDenied = (quote: Quote) => {
  return quote.status === 'denied' && quote.isInternalUser === true;
};

export const isQuoteCountered = (expirationTimer: number, quote: Quote) => {
  return (
    quote.status === 'countered' &&
    !isQuoteExpired(
      expirationTimer,
      quote.status,
      quote.isInternalUser,
      quote.createdAt
    )
  );
};

export const isQuoteAdminCountered = (
  expirationTimer: number,
  quote: Quote
) => {
  return (
    quote.status === 'countered' &&
    quote.isInternalUser &&
    !isQuoteExpired(
      expirationTimer,
      quote.status,
      quote.isInternalUser,
      quote.createdAt
    )
  );
};

export const isQuoteDeclined = (quote: Quote) => {
  return quote.status === 'denied';
};

export const isQuoteVoided = (quote: Quote) => {
  return (
    quote.status === 'cancelled' &&
    quote.comment === 'This load is no longer available.'
  );
};

export const getExpiredQuotes = (expirationTimer: number, quotes?: Quote[]) =>
  quotes
    ? quotes.filter((quote) =>
        isQuoteExpired(
          expirationTimer,
          quote.status,
          quote.isInternalUser,
          quote.createdAt
        )
      )
    : [];

export const getAdminAcceptedQuotes = (
  expirationTimer: number,
  quotes?: Quote[]
) =>
  quotes
    ? quotes.filter(
        (quote) =>
          quote.status === 'approved' ||
          (isQuoteAdminAccepted(expirationTimer, quote) &&
            !isQuoteExpired(
              expirationTimer,
              quote.status,
              quote.isInternalUser,
              quote.createdAt
            ))
      )
    : [];

export const getPendingQuotes = (expirationTimer: number, quotes?: Quote[]) =>
  quotes
    ? quotes.filter(
        (quote) =>
          isQuotePending(expirationTimer, quote) &&
          !isQuoteExpired(
            expirationTimer,
            quote.status,
            quote.isInternalUser,
            quote.createdAt
          )
      )
    : [];

export const getDeniedQuotes = (quotes?: Quote[]) =>
  quotes ? quotes.filter(isQuoteDeclined) : [];

export const getVoidedQuotes = (quotes?: Quote[]) =>
  quotes ? quotes.filter(isQuoteVoided) : [];

type ActionRequiredQuotes = [Quote[], Quote[], Quote[], Quote[]];
export const getActionRequiredQuotes = (
  expirationTimer: number,
  quotes?: Quote[]
) =>
  quotes
    ? quotes.reduce(
        (actionRequiredQuotes: ActionRequiredQuotes, quote) => {
          const [deniedQuotes, counteredQuotes, allActionRequiredQuotes] =
            actionRequiredQuotes;

          if (isQuoteAdminDenied(quote)) {
            deniedQuotes.push(quote);
            allActionRequiredQuotes.push(quote);
            return actionRequiredQuotes;
          }

          if (
            quote.status === 'countered' &&
            isQuoteAdminCountered(expirationTimer, quote)
          ) {
            counteredQuotes.push(quote);
            allActionRequiredQuotes.push(quote);
            return actionRequiredQuotes;
          }
          return actionRequiredQuotes;
        },
        [[], [], [], []]
      )
    : ([[], [], [], []] as ActionRequiredQuotes);
